import { useState, useEffect, useCallback } from 'react';

const SAVE_ENDPOINT = '/.netlify/functions/save-content';

export function useContent() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load content from public/content.json
  useEffect(() => {
    fetch('/content.json', { cache: 'no-store' })
      .then(r => r.json())
      .then(data => { setContent(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  // Verify the admin password against the serverless function.
  // Falls back to the local default ("admin") when the function isn't
  // available — e.g. running `npm start` locally without `netlify dev`.
  const verifyPassword = useCallback(async (password) => {
    try {
      const res = await fetch(SAVE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }), // no content => verify-only
      });
      if (res.status === 404) {
        return { ok: password === 'admin', devMode: true };
      }
      const data = await res.json().catch(() => ({}));
      return { ok: res.ok && !!data.ok, error: data.error };
    } catch (e) {
      // Network error (likely local dev with no functions) — soft fallback
      return { ok: password === 'admin', devMode: true };
    }
  }, []);

  // Save content via the serverless function (token + password live server-side).
  const saveContent = useCallback(async (newContent, password) => {
    try {
      const res = await fetch(SAVE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, content: newContent }),
      });

      if (res.status === 404) {
        // Function not deployed (local dev) — update local state only
        setContent(newContent);
        return { success: true, devMode: true };
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        return { success: false, error: data.error || `Save failed (${res.status})` };
      }

      setContent(newContent);
      return { success: true };
    } catch (err) {
      // Network error — likely local dev without functions running
      setContent(newContent);
      return { success: true, devMode: true };
    }
  }, []);

  return { content, loading, error, saveContent, verifyPassword, setContent };
}
